import React from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";

const RulesTab = ({
    rulesApi,
    pages,
    handlePageChange,
    onView,
    onEdit,
    onDelete,
}) => {
    if (rulesApi.error) return <ErrorState error={rulesApi.error} onRetry={() => rulesApi.refetch()} />;
    if (rulesApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    if (!Array.isArray(rulesApi.data) || rulesApi.data.length === 0)
        return <EmptyState message="Aucune règle configurée" color={SECTION_COLORS.rules.main} />;

    const page = pages[3];
    const paginated = rulesApi.data.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    return (
        <Box>
            <Grid container spacing={2}>
                {paginated.map((r) => {
                    const hasConditions = r.conditions && Object.keys(r.conditions).length > 0;
                    const actionsCount = Array.isArray(r.actions) ? r.actions.length : 0;

                    return (
                        <Grid item xs={12} sm={6} md={4} key={r.id}>
                            <Paper elevation={0} sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.rules.main}15`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.rules.main}06 0%, transparent 100%)`,
                                transition: "all 0.25s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: `0 8px 25px ${SECTION_COLORS.rules.main}12`,
                                    borderColor: `${SECTION_COLORS.rules.main}30`,
                                },
                            }}>
                                {/* Priority badge */}
                                <Tooltip title="Priorité" placement="left">
                                    <Box sx={{
                                        position: "absolute", top: 10, right: 10,
                                        width: 28, height: 28, borderRadius: "50%",
                                        background: SECTION_COLORS.rules.gradient,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        boxShadow: `0 2px 8px ${SECTION_COLORS.rules.main}40`, cursor: "default",
                                    }}>
                                        <Typography fontSize={11} fontWeight={800} color="#fff">{r.priority ?? 0}</Typography>
                                    </Box>
                                </Tooltip>

                                {/* Header */}
                                <Box display="flex" alignItems="center" gap={1} mb={1} mr={8}>
                                    <GavelIcon sx={{ color: SECTION_COLORS.rules.main, fontSize: 20, flexShrink: 0 }} />
                                    <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                        {r.event_type}
                                    </Typography>
                                </Box>

                                {/* Brand */}
                                <Typography fontSize={11} color="text.secondary" mb={1.5} sx={{ fontStyle: "italic" }}>
                                    Marque : <strong>{r.brand}</strong>
                                </Typography>

                                {/* Meta chips */}
                                <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                    {hasConditions && (
                                        <Chip label="Conditions" size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#fffbeb", color: "#92400e" }} />
                                    )}
                                    {actionsCount > 0 && (
                                        <Chip label={`${actionsCount} action${actionsCount > 1 ? "s" : ""}`} size="small"
                                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#f0fdf4", color: "#15803d" }} />
                                    )}
                                </Box>

                                {/* Footer */}
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <StatusChip active={r.active} />
                                    <Box display="flex" gap={0.5}>
                                        <Tooltip title="Voir le détail">
                                            <IconButton size="small" onClick={() => onView(r)}
                                                sx={{
                                                    color: SECTION_COLORS.rules.main, bgcolor: `${SECTION_COLORS.rules.main}10`,
                                                    "&:hover": { bgcolor: `${SECTION_COLORS.rules.main}20` }, width: 30, height: 30
                                                }}>
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => onEdit(r)}
                                                sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" onClick={() => onDelete(r)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {rulesApi.data.length > ROWS_PER_PAGE && (
                <TablePagination component="div" count={rulesApi.data.length} page={page}
                    onPageChange={handlePageChange(3)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
            )}
        </Box>
    );
};

export default RulesTab;
